import { useTokenContext } from "@cyclos-io/swap-ui";
import { useWalletKit } from "@gokiprotocol/walletkit";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  CloseRounded,
  FiberManualRecord,
  RefreshRounded,
} from "@material-ui/icons";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { CYSaddress, PoolType } from "../../constants";
import useStaking from "../../hooks/useStaking";
import useWallet from "../../hooks/useWallet";

const useStyles = makeStyles((theme) => ({
  box: {
    width: "100%",
    height: "100%",
    borderRadius: theme.spacing(0.8),
    padding: theme.spacing(2),
    textAlign: "center",
  },
  chipGreen: {
    borderRadius: theme.spacing(0.8),
    fontSize: theme.spacing(1.6),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    // marginBottom: theme.spacing(3),
  },
  chipWarning: {
    borderRadius: theme.spacing(0.8),
    fontSize: theme.spacing(1.6),
    backgroundColor: theme.palette.secondary.main,
    // marginBottom: theme.spacing(3),
  },
  buttonStyle: {
    "&:disabled": {
      cursor: "not-allowed",
      pointerEvents: "all !important",
    },
  },
}));

function StakeCard({
  poolDetails,
  description,
  title,
  poolType,
  stakeAccountDetails,
}: any) {
  const classes = useStyles();
  const { connect } = useWalletKit();
  const { isConnected, connection, wallet } = useWallet();
  const [inputStake, setInputStake] = useState<number>(0);
  const [inputUnstake, setInputUnstake] = useState<number>(0);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { stake, unstake, claim, setReloadNL, setReload2M, getPendingReward } =
    useStaking();
  const [rewards, setRewards] = useState("");
  const [loading, setLoading] = useState(false);
  const { userTokens } = useTokenContext();

  const cysBalance =
    userTokens && userTokens !== "fetching"
      ? userTokens?.[CYSaddress]?.tokenAmount
      : 0;

  useEffect(() => {
    setRewards(getPendingReward(poolType).toFixed(6));
  }, [stakeAccountDetails]);

  const formattedStakeAmount = inputStake
    ? inputStake?.toLocaleString("fullwide", {
        maximumFractionDigits: 6,
        useGrouping: false,
      })
    : inputStake;

  const formattedUnstakeAmount = inputUnstake
    ? inputUnstake?.toLocaleString("fullwide", {
        maximumFractionDigits: 6,
        useGrouping: false,
      })
    : inputUnstake;

  const handleStake = async (input: number, poolType: PoolType) => {
    if (!input || input <= 0) {
      enqueueSnackbar("Enter amount to stake", {
        variant: "error",
      });
      return;
    }
    setLoading(true);
    stake(input, poolType)
      .then((tx: string) => {
        enqueueSnackbar("Transaction Confirmed", {
          variant: "success",
          persist: true,
          action: (key) => (
            <>
              <Button
                color="inherit"
                component="a"
                target="_blank"
                rel="noopener"
                href={`https://solscan.io/tx/${tx}`}
              >
                View on Explorer
              </Button>
              <IconButton onClick={() => closeSnackbar(key)} size="small">
                <CloseRounded />
              </IconButton>
            </>
          ),
        });
        setInputStake(0);
      })
      .catch((err: any) => {
        console.log(err);
        enqueueSnackbar(err?.message ?? "Something went wrong", {
          variant: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  const handleUnstake = async (input: number, poolType: PoolType) => {
    if (
      poolType === PoolType.TWOLOCK &&
      Date.now() <
        new Date(
          +(stakeAccountDetails?.maturityTime.toString() ?? 0) * 1000
        ).getTime()
    ) {
      enqueueSnackbar("Cannot unstake before unlock period.", {
        variant: "warning",
      });
      return;
    }
    if (input > +(stakeAccountDetails?.balanceStaked.toString() ?? 0) / 1e6) {
      enqueueSnackbar("Unstake amount should be less than staked amount", {
        variant: "error",
      });
      return;
    }
    if (!input || input <= 0) {
      enqueueSnackbar("Enter amount to unstake", {
        variant: "error",
      });
      return;
    }
    setLoading(true);
    unstake(input, poolType)
      .then((tx: string) => {
        enqueueSnackbar("Transaction Confirmed", {
          variant: "success",
          persist: true,
          action: (key) => (
            <>
              <Button
                color="inherit"
                component="a"
                target="_blank"
                rel="noopener"
                href={`https://solscan.io/tx/${tx}`}
              >
                View on Explorer
              </Button>
              <IconButton onClick={() => closeSnackbar(key)} size="small">
                <CloseRounded />
              </IconButton>
            </>
          ),
        });
        setInputUnstake(0);
      })
      .catch((err: any) => {
        console.log(err);
        enqueueSnackbar(err?.message ?? "Something went wrong", {
          variant: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  const handleClaim = async (poolType: PoolType) => {
    if (
      Date.now() <
      new Date(
        +(stakeAccountDetails?.maturityTime.toString() ?? 0) * 1000
      ).getTime()
    ) {
      enqueueSnackbar("Cannot claim rewards before unlock period.", {
        variant: "warning",
      });
      return;
    }
    setLoading(true);
    claim(poolType)
      .then((tx: string) => {
        enqueueSnackbar("Transaction Confirmed", {
          variant: "success",
          persist: true,
          action: (key) => (
            <>
              <Button
                color="inherit"
                component="a"
                target="_blank"
                rel="noopener"
                href={`https://solscan.io/tx/${tx}`}
              >
                View on Explorer
              </Button>
              <IconButton onClick={() => closeSnackbar(key)} size="small">
                <CloseRounded />
              </IconButton>
            </>
          ),
        });
      })
      .catch((err: any) => {
        console.log(err);
        enqueueSnackbar(err?.message || "Something went wrong", {
          variant: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  const handleReload = () => {
    setLoading(true);
    if (poolType === PoolType.NOLOCK) {
      setReloadNL((p: boolean) => !p);
    } else if (poolType === PoolType.TWOLOCK) {
      setReload2M((p: boolean) => !p);
    }
    setTimeout(() => setLoading(false), 1000);
  };

  return (
    <Paper className={classes.box}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        m={1}
        mt={0}
      >
        <Box>
          <Box display="flex" alignItems="baseline">
            <Typography variant="h6">{title}</Typography>{" "}
            &nbsp;&nbsp;&nbsp;&nbsp;
            <Chip
              label={!poolDetails?.paused ? "Active" : "Ended"}
              size="small"
              icon={<FiberManualRecord  style={{color: "#2C2F42e8", width: "0.7rem"}} />}
              className={
                !poolDetails?.paused ? classes.chipGreen : classes.chipWarning
              }
            />
          </Box>
          <Typography color="textSecondary" variant="caption">
            {description}
          </Typography>
        </Box>
        {isConnected && (
          <Tooltip
            title={
              <Typography variant="caption">
                {/* Rewards auto-refresh after {timerProgress} seconds. <br /> */}
                Click to Refresh.
              </Typography>
            }
          >
            <IconButton onClick={handleReload} size="small">
              {loading ? (
                <CircularProgress size="1.5rem" />
              ) : (
                <RefreshRounded />
              )}
            </IconButton>
          </Tooltip>
        )}

        {/* <Chip
        label={!poolDetails?.paused ? "Active" : "Ended"}
        size="small"
        icon={<FiberManualRecord />}
        className={!poolDetails?.paused ? classes.chipGreen : classes.chipWarning}
      /> */}
      </Box>
      {!isConnected && (
        <>
          <Box my={4}>
            <Typography gutterBottom color="textSecondary" variant="body1">
              APR
            </Typography>
            <Typography
              variant="h2"
              style={{
                fontWeight: 800,
              }}
            >
              {poolDetails.apr == Infinity ? (
                "-"
              ) : (
                <>
                  {poolDetails.apr?.toLocaleString(undefined, {
                    style: "percent",
                  }) ?? "-"}
                  &nbsp;
                  {poolDetails.apr && <small>%</small>}
                </>
              )}
            </Typography>
          </Box>
          <Box mb={4}>
            <Typography gutterBottom color="textSecondary" variant="body1">
              Total Value Locked
            </Typography>
            <Typography variant="h4">
              {poolDetails?.tvl?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) ?? "0"}
              &nbsp;
              <small>CYS</small>
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={connect}
          >
            Connect Wallet to Stake
          </Button>
        </>
      )}
      {isConnected && (
        <>
          <Box
            display="flex"
            my={3}
            justifyContent="space-around"
            alignItems="center"
          >
            <Box>
              <Typography color="textSecondary" variant="caption">
                TVL
              </Typography>
              <Typography variant="h6">
                {poolDetails?.tvl?.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }) ?? "0"}
                &nbsp;
                <small>CYS</small>
              </Typography>
            </Box>
            <Box>
              <Typography color="textSecondary" variant="caption">
                APR
              </Typography>
              {poolDetails.apr == Infinity ? (
                <Typography variant="h6">-</Typography>
              ) : (
                <Typography variant="h6">
                  {poolDetails.apr?.toLocaleString(undefined, {
                    style: "percent",
                  }) ?? "-"}
                  &nbsp;
                  {poolDetails.apr && <small>%</small>}
                </Typography>
              )}
            </Box>
          </Box>
          <Box my={3} justifyContent="center">
            <Typography
              variant="h3"
              style={{
                fontWeight: 700,
              }}
            >
              {parseFloat(rewards)?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
              })}
            </Typography>
            <Typography color="textSecondary" variant="subtitle2">
              CYS Earned
            </Typography>
          </Box>
          <Box
            display="flex"
            my={3}
            justifyContent="space-around"
            alignItems="center"
          >
            <Box>
              <Typography color="textSecondary" variant="caption">
                Staked
              </Typography>
              <Typography variant="h6">
                {(
                  parseFloat(
                    stakeAccountDetails?.balanceStaked.toString() ?? 0
                  ) / 1e6
                )?.toLocaleString(undefined, {
                  maximumFractionDigits: 6,
                })}
                &nbsp;<small>CYS</small>
              </Typography>
            </Box>
            <Box>
              <Typography color="textSecondary" variant="caption">
                Unlock Time
              </Typography>
              <Typography variant="h6">
                {poolType === PoolType.NOLOCK ? (
                  <span>-</span>
                ) : (
                  <span>
                    {!stakeAccountDetails?.maturityTime
                      ? "-"
                      : new Date(
                          parseInt(
                            stakeAccountDetails?.maturityTime.toString() ?? 0
                          ) * 1000
                        )
                          .toString()
                          .slice(3, 15)}
                  </span>
                )}
              </Typography>
            </Box>
          </Box>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} style={{ marginTop: "0.5rem" }}>
              <TextField
                size="small"
                type="number"
                variant="outlined"
                fullWidth
                label="Stake Amount"
                inputProps={{
                  min: 0,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        style={{
                          maxWidth: "2.6rem",
                          maxHeight: "1.4rem",
                          minWidth: "2.6rem",
                          minHeight: "1.4rem",
                          fontSize: "0.62rem",
                          marginRight: -5,
                        }}
                        color="primary"
                        onClick={() => setInputStake(cysBalance ?? 0)}
                      >
                        Max
                      </Button>
                    </InputAdornment>
                  ),
                }}
                value={formattedStakeAmount}
                onChange={(e) => setInputStake(parseFloat(e.target.value))}
              />
              <Box m={1} />
              <Button
                variant="contained"
                fullWidth
                color="primary"
                onClick={() => handleStake(inputStake, poolType)}
              >
                Stake
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} style={{ marginTop: "0.5rem" }}>
              <TextField
                size="small"
                type="number"
                variant="outlined"
                fullWidth
                label="Unstake Amount"
                inputProps={{
                  min: 0,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        style={{
                          maxWidth: "2.6rem",
                          maxHeight: "1.4rem",
                          minWidth: "2.6rem",
                          minHeight: "1.4rem",
                          fontSize: "0.62rem",
                          marginRight: -5,
                        }}
                        color="primary"
                        disabled={
                          parseInt(
                            stakeAccountDetails?.balanceStaked.toString() ?? 0
                          ) === 0
                        }
                        onClick={() =>
                          setInputUnstake(
                            parseFloat(
                              stakeAccountDetails?.balanceStaked.toString() ?? 0
                            ) / 1e6
                          )
                        }
                      >
                        Max
                      </Button>
                    </InputAdornment>
                  ),
                }}
                disabled={
                  parseInt(
                    stakeAccountDetails?.balanceStaked.toString() ?? 0
                  ) === 0
                }
                value={formattedUnstakeAmount}
                onChange={(e) => setInputUnstake(parseFloat(e.target.value))}
              />
              <Box my={1} />
              <Button
                variant="contained"
                fullWidth
                className={classes.buttonStyle}
                color="primary"
                onClick={() => handleUnstake(inputUnstake, poolType)}
                disabled={
                  parseInt(
                    stakeAccountDetails?.balanceStaked.toString() ?? 0
                  ) === 0
                }
              >
                Unstake
              </Button>
            </Grid>
          </Grid>
          <Box mb={2} />
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={() => handleClaim(poolType)}
          >
            Claim Rewards
          </Button>
        </>
      )}
    </Paper>
  );
}

export default StakeCard;
